import type { Product, User } from 'payments-api/model';

export const PLATFORM_PAYOUT_USER = 'Platform';

type DropdownOption = { value: string; label: string };

/**
 * Find a product by its code
 */
export const findProductByCode = (
  productCode: string,
  products: Product[]
): Product | undefined => products.find((p) => p.code === productCode);

/**
 * Gets the paid_to_roles for a product
 */
export const getProductPaidToRoles = (
  productCode: string,
  products: Product[]
): string[] => {
  const product = findProductByCode(productCode, products);
  if (!product) return [];
  return product.paid_to_roles.map((role) => role.name);
};

/**
 * Result of determining payout party options and auto-selection for a product
 */
export type PayoutUserConfig = {
  /** The payout party options to show in the dropdown */
  options: DropdownOption[];
  /** Whether the dropdown should be disabled */
  isDisabled: boolean;
  /** The auto-selected payout party value */
  autoSelectedValue: string | undefined;
};

/**
 * Given a selected product, returns the payout party configuration:
 * - For products with paid_to_roles: enabled dropdown filtered by user roles
 * - For no product selected: enabled dropdown with all users
 */
export const getPayoutUserConfig = (
  productCode: string | undefined,
  products: Product[],
  users: User[]
): PayoutUserConfig => {
  if (!productCode) {
    // No product selected - show all users
    return {
      options: users.map((u) => ({
        value: u.external_id,
        label: u.external_id,
      })),
      isDisabled: false,
      autoSelectedValue: undefined,
    };
  }

  const product = findProductByCode(productCode, products);
  if (!product) {
    // Product not found - show all options
    return {
      options: users.map((u) => ({
        value: u.external_id,
        label: u.external_id,
      })),
      isDisabled: false,
      autoSelectedValue: undefined,
    };
  }

  // Filter users by roles matching product's paid_to_roles
  const paidToRoles = product.paid_to_roles.map(({ name }) => name);
  const filteredUsers = users.filter(
    (u) => u.role && paidToRoles.includes(u.role)
  );

  return {
    options: filteredUsers.map((u) => ({
      value: u.external_id,
      label: u.external_id,
    })),
    isDisabled: false,
    autoSelectedValue: undefined,
  };
};

/**
 * Handles the logic when a product is selected for an invoice item.
 * Returns the updates that should be applied to the item.
 */
export const getProductSelectionUpdates = (
  productCode: string,
  products: Product[]
): { payoutUser: string } | undefined => {
  const product = findProductByCode(productCode, products);
  if (!product) return undefined;

  // Clear the payout user so user must select from filtered options
  return { payoutUser: '' };
};

/**
 * Returns user dropdown options for a product and line item type.
 * - Payin: users whose role is in the product's paid_by_roles
 * - Payout: users whose role is in the product's paid_to_roles
 */
export const getUsersForProductByItemType = (
  productCode: string,
  itemType: 'payin' | 'payout',
  products: Product[],
  users: User[]
): DropdownOption[] => {
  const product = findProductByCode(productCode, products);
  if (!product) return [];

  const roleNames =
    itemType === 'payin'
      ? product.paid_by_roles.map((r) => r.name)
      : product.paid_to_roles.map((r) => r.name);

  return users
    .filter((u) => u.role && roleNames.includes(u.role))
    .map((u) => ({ value: u.external_id, label: u.external_id }));
};
